import { SelectionMode } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPModal, IPProgressIndicator, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ButtonDialogFooter from 'components/Footer/ButtonDialogFooter';
import { isAbortOrCancelError } from 'components/Notification/ErrorMessageHelper';
import FilePicker from 'components/Pickers/FilePicker';
import { cancelIcon, globalStackTokensGapMedium, globalTextStylesCardRed } from 'globalStyles';
import { ProviderItem } from 'models/documentConnection';
import { getListCapabilities, getProviderName } from 'models/documentProvider';
import ResourceLink from 'models/resourceLink';
import ResourceList, { ResourceListType } from 'models/resourceList';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateListFolder, apiUploadListItem, UploadConflictBehavior } from 'services/Api/linkContentService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { BrokeredDirectoryExplorer } from '../Brokered/BrokeredDirectoryExplorer';
import { notifyBrokeredError } from '../Brokered/brokeredErrors';
import { UploadFile } from './LinkCreatorDocumentLibrary';
import { v4 as uuidv4 } from 'uuid';

interface ILinkCreatorBrokeredProps {
  isOpen: boolean;
  list: ResourceList;
  //the folder in the list where the explorer opens and the upload goes, instead of the list's root
  uploadFolderId?: string;
  uploadFolderName?: string;
  dismiss: () => void;
  addLinks: (links: ResourceLink[]) => void;
  listLinks: ResourceLink[];
  existingLinks: ResourceLink[];
  listLoading: boolean;
  linkToEdit?: ResourceLink;
  hideWholeList?: boolean;
  singleSelect?: boolean;
}

/**
 * Adds a library item to a category held by a document provider, in the same shape as the
 * SharePoint picker beside it: the explorer with its command bar on top, the shared file
 * drop zone underneath, and one footer that adds whichever of the two was used.
 *
 * Browsing stays inside the category root. The category permissions are the access control
 * for a tenant-level connection, so letting the picker wander outside them would hand every
 * author the whole connected account.
 */
const LinkCreatorBrokered = (props: ILinkCreatorBrokeredProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['library', 'documentproviders', 'translation']);
  const [selected, setSelected] = useState<ProviderItem | undefined>(undefined);
  const rootFolderId = props.uploadFolderId ?? props.list.spDriveItemId;
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(rootFolderId);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadedCount, setUploadedCount] = useState<number>(0);
  const [fileTypeError, setFileTypeError] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [selectedFileNames, setSelectedFileNames] = useState<Record<string, string | undefined>>({});
  const [fileNameErrors, setFileNameErrors] = useState<Record<string, string | undefined>>({});
  const [pendingFileId, setPendingFileId] = useState<string | undefined>(undefined);
  const [finishedFileIds, setFinishedFileIds] = useState<string[]>([]);
  // the batch in flight; Cancel aborts it, the way the SharePoint picker cancels its Graph upload
  const uploadAbort = useRef<AbortController | undefined>(undefined);

  const capabilities = getListCapabilities(props.list, appContext.globalDataCache);
  const providerName = getProviderName(props.list.providerType);

  const getLinkForItem = (item: ProviderItem): ResourceLink => {
    const output = props.linkToEdit ? props.linkToEdit.clone() : new ResourceLink();

    output.listId = props.list.listId;
    output.list = props.list;
    output.linkName = item.name;
    output.linkURL = item.webUrl ?? '';

    // the broker reads the item id by list type, the same way SharePoint does: a page category
    // (Notion, Loop) keeps it in pageId, a file category in driveItemId
    if (props.list.listType === ResourceListType.SitePageLibrary) {
      output.pageId = item.id;
      output.driveItemId = undefined;
    } else {
      output.driveItemId = item.id;
      output.pageId = undefined;
    }

    return output;
  };

  const onPickFiles = (items: File[]) => {
    const existingNames = new Set(selectedFiles.map((f) => f.file.name));
    const filesWithId: UploadFile[] = items
      .filter((f) => !existingNames.has(f.name))
      .map((f) => ({ id: uuidv4(), file: f }));

    setSelectedFiles([...selectedFiles, ...filesWithId]);
    setFileTypeError(false);

    const names: Record<string, string | undefined> = { ...selectedFileNames };
    const errors: Record<string, string | undefined> = { ...fileNameErrors };
    filesWithId.forEach(({ id, file }) => {
      names[id] = file.name;
      errors[id] = validateFileName(file.name);
    });
    setSelectedFileNames(names);
    setFileNameErrors(errors);
  };

  const onChangeFileName = (item: File, newName: string | undefined, idx: number) => {
    const uploadFile = selectedFiles[idx];
    if (!uploadFile) return;
    setSelectedFileNames((prev) => ({ ...prev, [uploadFile.id]: newName }));
    setFileNameErrors((prev) => ({ ...prev, [uploadFile.id]: validateFileName(newName) }));
  };

  const onRemoveFile = (item: File) => {
    const idx = selectedFiles.findIndex((f) => f.file === item);
    if (idx === -1) return;
    const id = selectedFiles[idx].id;
    setSelectedFiles(selectedFiles.filter((_, i) => i !== idx));

    setSelectedFileNames((prev) => {
      const copy = { ...prev };
      delete copy[id];

      return copy;
    });
    setFileNameErrors((prev) => {
      const copy = { ...prev };
      delete copy[id];

      return copy;
    });
  };

  // Providers reject far less than SharePoint does, so only the name being usable at all is
  // checked here. A name the provider still refuses comes back as a DOCPROV error.
  const validateFileName = (newName: string | undefined): string | undefined => {
    if (!newName || newName.trim() === '') return t('library:Picker.FileNameErrorEmpty');

    const invalidChars = '"*:<>?/\\|';
    for (let i = 0; i < newName.length; i++) {
      if (invalidChars.indexOf(newName.charAt(i)) !== -1) {
        return t('library:Picker.FileNameErrorInvalid', { chars: invalidChars });
      }
    }

    return undefined;
  };

  // Files already at the provider must reach the parent even when a later one fails, or they
  // sit in the category's folder with nothing pointing at them.
  const commitAndClose = (newLinks: ResourceLink[]) => {
    if (newLinks.length > 0) props.addLinks(newLinks);
    props.dismiss();
  };

  const onSubmit = async () => {
    if (disableAdd()) return;
    const newLinks: ResourceLink[] = [];
    const abort = new AbortController();
    uploadAbort.current = abort;

    try {
      if (selectedFiles.length > 0) {
        setIsUploading(true);
        setUploadedCount(0);
        setFinishedFileIds([]);

        for (let i = 0; i < selectedFiles.length; i++) {
          const { file, id } = selectedFiles[i];
          setPendingFileId(id);

          // a token per file: a long batch outlives one token, and the Graph uploader does the same
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);

          const result = await apiUploadListItem(
            props.list.listId,
            file,
            currentFolderId,
            UploadConflictBehavior.Rename,
            true,
            selectedFileNames[id] ?? file.name,
            accessToken,
            appContext.globalDataCache,
            abort.signal,
          );

          if (result.link) newLinks.push(result.link);
          setFinishedFileIds((prev) => [...prev, id]);
          setUploadedCount(i + 1);

          // a cancel that lands while a file is in flight still completes that file; stop
          // before the next one
          if (abort.signal.aborted) {
            commitAndClose(newLinks);

            return;
          }
        }
      }

      if (selected && !selected.isFolder) {
        newLinks.push(getLinkForItem(selected));
      }

      commitAndClose(newLinks);
    } catch (err) {
      // a cancel is a user action: nothing to report, and the files that did land keep their links
      if (isAbortOrCancelError(AppError.fromApiError(err))) {
        commitAndClose(newLinks);

        return;
      }

      notifyBrokeredError(appContext, err, t);
      // whatever did land keeps its link; with nothing uploaded the modal stays open to retry
      if (newLinks.length > 0) commitAndClose(newLinks);
    } finally {
      uploadAbort.current = undefined;
      setIsUploading(false);
      setPendingFileId(undefined);
    }
  };

  const cancelUpload = () => {
    uploadAbort.current?.abort();
  };

  const onSubmitWholeListItem = () => {
    const existingLink = props.listLinks.find((l) => l.isWholeList());
    const newLink = existingLink ?? new ResourceLink();
    if (!existingLink) newLink.setWholeList(props.list);

    props.addLinks([newLink]);
    props.dismiss();
  };

  const disableAdd = (): boolean => {
    if (isUploading) return true;

    if (selectedFiles.length > 0) {
      return (
        fileTypeError ||
        Object.values(fileNameErrors).some((e) => !!e) ||
        Object.values(selectedFileNames).some((n) => !n || n.trim() === '')
      );
    }

    return !selected || selected.isFolder || !capabilities.linkExisting;
  };

  const createFolder = async (parentFolderId: string | undefined, name: string) => {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    await apiCreateListFolder(props.list.listId, parentFolderId, name, accessToken);
  };

  const percentage = selectedFiles.length > 0 ? uploadedCount / selectedFiles.length : 0;

  //
  // Main render
  //
  return (
    <IPModal isBlocking isOpen={props.isOpen} onDismiss={props.dismiss}>
      <IPStack verticalFill styles={{ root: { height: '90vh', width: 1000, minWidth: 320, maxWidth: '90vw', padding: 20 } }}>
        {/* Header */}
        <IPStack.Item>
          <IPStack horizontal horizontalAlign={'space-between'}>
            <IPStack.Item>
              {!props.linkToEdit && (
                <IPText variant="xLarge">
                  {t('library:LinkComponents.AddItemOf', {
                    name: props.uploadFolderName ? `${props.list.name} / ${props.uploadFolderName}` : props.list.name,
                  })}
                </IPText>
              )}
              {props.linkToEdit && (
                <IPText variant="xLarge">
                  {t('library:LinkComponents.ReplaceItem', { name: props.linkToEdit.linkName, list: props.list.name })}
                </IPText>
              )}
            </IPStack.Item>
            <IPStack.Item>
              {/* while a batch runs the cross cancels it rather than leaving it running behind a closed modal */}
              <IPIconButton iconProps={cancelIcon} onClick={isUploading ? cancelUpload : props.dismiss} />
            </IPStack.Item>
          </IPStack>
          <IPSeparator />
        </IPStack.Item>
        {/* Body */}
        <IPStack.Item grow>
          <IPStack verticalFill tokens={globalStackTokensGapMedium}>
            <IPStack.Item grow>
              <BrokeredDirectoryExplorer
                connectionId={props.list.connectionId ?? -1}
                containerId={props.list.spDriveId}
                rootFolderId={rootFolderId}
                providerName={providerName}
                disabled={isUploading}
                selectionMode={SelectionMode.single}
                onCreateFolder={capabilities.createFolder ? createFolder : undefined}
                onSelectItems={(items: ProviderItem[]) => setSelected(items[0])}
                onNavigate={(_containerId, folderId) => {
                  setCurrentFolderId(folderId ?? rootFolderId);
                  setSelected(undefined);
                }}
              />
            </IPStack.Item>
            {capabilities.upload && (
              <IPStack.Item>
                <FilePicker
                  selectedFiles={selectedFiles}
                  selectedFileNames={selectedFileNames}
                  onSelect={onPickFiles}
                  onChangeName={onChangeFileName}
                  onRemove={onRemoveFile}
                  fileNameErrors={fileNameErrors}
                  onFileTypeError={() => setFileTypeError(true)}
                  actionPending={isUploading}
                  pendingFileId={pendingFileId}
                  finishedFileIds={finishedFileIds}
                />
              </IPStack.Item>
            )}
            {fileTypeError && (
              <IPStack.Item>
                <IPText styles={globalTextStylesCardRed}>{t('library:LinkComponents.FileTypeError')}</IPText>
              </IPStack.Item>
            )}
            {isUploading && selectedFiles.length > 0 && (
              <IPStack.Item>
                <IPProgressIndicator
                  label={t('library:Picker.UploadingFiles', {
                    current: Math.min(uploadedCount + 1, selectedFiles.length),
                    total: selectedFiles.length,
                  })}
                  barHeight={5}
                  percentComplete={percentage}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        {/* Footer */}
        <IPStack.Item>
          <ButtonDialogFooter
            primaryText={props.linkToEdit ? t('translation:General.Button.Save') : t('translation:General.Button.Add')}
            secondaryText={t('translation:General.Button.Cancel')}
            onSubmit={() => {
              onSubmit();
            }}
            onCancel={isUploading ? cancelUpload : props.dismiss}
            buttonPosition="end"
            disablePrimary={disableAdd}
            tertiaryText={
              props.hideWholeList || !capabilities.wholeList ? undefined : t('library:LinkComponents.WholeList')
            }
            tertiaryTooltip={
              props.existingLinks.find((l) => l.isWholeList()) !== undefined
                ? t('library:LinkComponents.Validate.ItemExists')
                : undefined
            }
            disableTertiary={() => props.existingLinks.find((l) => l.isWholeList()) !== undefined}
            onTertiary={onSubmitWholeListItem}
          />
        </IPStack.Item>
      </IPStack>
    </IPModal>
  );
};

export default LinkCreatorBrokered;
